/**
 * Blackwaxx&Co product page behaviour (sections/bw-main-product.liquid).
 * - Variant choice from the photo swatches and the pills (both drive the same selection): updates ?variant=, the
 *   price, compare-at, combo note, Afterpay quarter, buttons, stock line, mobile buy bar and the gallery order.
 * - Quantity stepper and the total in the button.
 * - Add to cart through the shared @bw/cart module, so the header count and Horizon's cart drawer follow.
 * - Phone gallery: one photo at a time with arrows, a count and thumbnails (CSS shows a stack on larger screens).
 */
import { addToCart } from '@bw/cart';

class BwProduct extends HTMLElement {
  #options = [];
  #variant = null;
  #index = 0;

  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-bw-variants]').textContent);
    this.optionEls = [...this.querySelectorAll('[data-bw-option]')];
    this.idInput = this.querySelector('[data-bw-variant-id]');
    this.qtyInput = this.querySelector('[data-bw-qty]');
    this.stack = this.querySelector('[data-bw-stack]');

    const initial = this.variants.find((v) => String(v.id) === this.idInput?.value) ?? this.variants[0];
    this.#variant = initial;
    this.#options = [...initial.options];

    this.addEventListener('click', this.#onClick);
    this.querySelector('form')?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#add();
    });
    this.qtyInput?.addEventListener('input', () => this.#syncTotal());
    this.qtyInput?.addEventListener('change', () => {
      this.qtyInput.value = String(this.quantity);
      this.#syncTotal();
    });
    this.#showIndex(0);
  }

  get quantity() {
    const n = parseInt(this.qtyInput?.value ?? '1', 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const value = target.closest('[data-bw-value]');
    if (value) return this.#choose(Number(value.dataset.optionIndex), value.dataset.bwValue);

    if (target.closest('[data-bw-qty-down]')) return this.#setQty(this.quantity - 1);
    if (target.closest('[data-bw-qty-up]')) return this.#setQty(this.quantity + 1);
    if (target.closest('[data-bw-add-mobile]')) return this.#add();

    if (target.closest('[data-bw-gallery-prev]')) return this.#showIndex(this.#index - 1);
    if (target.closest('[data-bw-gallery-next]')) return this.#showIndex(this.#index + 1);
    const thumb = target.closest('[data-bw-thumb]');
    if (thumb) {
      const items = this.#items;
      const i = items.findIndex((el) => el.dataset.mediaId === thumb.dataset.mediaId);
      if (i > -1) this.#showIndex(i);
    }
  };

  /* ---------- variants ---------- */

  #choose(optionIndex, value) {
    const next = [...this.#options];
    next[optionIndex] = value;
    let match = this.variants.find((v) => v.options.every((o, i) => o === next[i]));
    if (!match) {
      // No such combination: keep the choice just made and the closest other options
      const candidates = this.variants.filter((v) => v.options[optionIndex] === value);
      match = candidates.sort(
        (a, b) => b.options.filter((o, i) => o === next[i]).length - a.options.filter((o, i) => o === next[i]).length
      )[0];
    }
    if (match) this.#apply(match);
  }

  #apply(variant) {
    this.#variant = variant;
    this.#options = [...variant.options];
    if (this.idInput) this.idInput.value = String(variant.id);

    const url = new URL(location.href);
    url.searchParams.set('variant', String(variant.id));
    history.replaceState({}, '', url);

    const shownPrice = variant.sold ? 'SOLD' : variant.price;
    this.#text('[data-bw-price]', shownPrice);
    this.#text('[data-bw-price-mobile]', shownPrice);
    const afterpay = this.querySelector('[data-bw-afterpay]');
    if (afterpay) afterpay.hidden = Boolean(variant.sold);
    this.#text('[data-bw-quarter]', variant.quarter);
    this.#text('[data-bw-short]', variant.short);
    const compare = this.querySelector('[data-bw-compare]');
    if (compare) {
      compare.hidden = !variant.compareAt || variant.sold;
      compare.textContent = variant.compareAt || '';
    }
    const combo = this.querySelector('[data-bw-combo]');
    if (combo) combo.hidden = !variant.combo;

    const single = this.optionEls.length === 1;
    this.optionEls.forEach((el, i) => {
      el.querySelector('[data-bw-selected-name]').textContent = single ? variant.title : variant.options[i];
      el.querySelectorAll('[data-bw-value]').forEach((btn) => {
        const on = btn.dataset.bwValue === variant.options[i];
        if (btn.getAttribute('role') === 'radio') btn.setAttribute('aria-checked', String(on));
        else btn.setAttribute('aria-pressed', String(on));
      });
    });

    const stock = this.querySelector('[data-bw-stock-line]');
    if (stock) {
      stock.classList.toggle('is-out', !variant.available);
      const text = stock.querySelector('[data-bw-stock-text]');
      if (text) text.textContent = variant.available ? stock.dataset.inText : stock.dataset.outText;
    }

    this.#syncButtons();
    this.#orderGallery(variant.mediaId);
  }

  #text(selector, value) {
    this.querySelectorAll(selector).forEach((el) => {
      el.textContent = value;
    });
  }

  /* ---------- quantity, total, buttons ---------- */

  #setQty(n) {
    if (this.qtyInput) this.qtyInput.value = String(Math.max(1, n));
    this.#syncTotal();
  }

  /** Format a total in the store's own money format by swapping the number inside the variant's formatted price. */
  #money(cents) {
    const number = (cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return this.#variant.price.replace(/\d[\d.,\s]*/, number);
  }

  #syncTotal() {
    const total = this.querySelector('[data-bw-total]');
    if (total) total.textContent = this.quantity === 1 ? this.#variant.price : this.#money(this.#variant.priceCents * this.quantity);
  }

  #syncButtons() {
    const v = this.#variant;
    const add = this.querySelector('[data-bw-add]');
    const label = this.querySelector('[data-bw-add-label]');
    if (add) add.disabled = !v.available;
    if (label) {
      const hasTotal = label.dataset.hasTotal === 'true';
      if (v.available) {
        label.innerHTML = hasTotal
          ? `Add to cart &middot; <span data-bw-total>${v.price}</span>`
          : 'Add to cart';
        this.#syncTotal();
      } else {
        label.textContent = 'Sold out';
      }
    }
    const mobile = this.querySelector('[data-bw-add-mobile]');
    if (mobile) {
      mobile.disabled = !v.available;
      mobile.textContent = v.available ? 'Add to cart' : 'Sold out';
    }
  }

  async #add() {
    const v = this.#variant;
    if (!v.available) return;
    const error = this.querySelector('[data-bw-error]');
    const buttons = this.querySelectorAll('[data-bw-add], [data-bw-add-mobile]');
    buttons.forEach((b) => {
      b.disabled = true;
      b.setAttribute('aria-busy', 'true');
    });
    if (error) error.hidden = true;

    const result = await addToCart({ id: v.id, quantity: this.quantity, source: this, productId: this.dataset.productId });

    buttons.forEach((b) => {
      b.disabled = !this.#variant.available;
      b.removeAttribute('aria-busy');
    });
    if (!result.ok && error) {
      error.textContent = result.message || 'That could not be added to your cart.';
      error.hidden = false;
    }
  }

  /* ---------- gallery ---------- */

  get #items() {
    return [...this.stack.querySelectorAll('[data-bw-media]')];
  }

  #orderGallery(mediaId) {
    const items = this.#items;
    items.sort((a, b) => Number(a.dataset.position) - Number(b.dataset.position));
    if (mediaId != null) {
      const i = items.findIndex((el) => el.dataset.mediaId === String(mediaId));
      if (i > 0) items.unshift(...items.splice(i, 1));
    }
    items.forEach((el) => this.stack.append(el));
    this.#showIndex(0);
  }

  #showIndex(i) {
    const items = this.#items;
    if (!items.length) return;
    this.#index = (i + items.length) % items.length;
    items.forEach((el, n) => el.classList.toggle('is-current', n === this.#index));
    const currentId = items[this.#index].dataset.mediaId;
    this.querySelectorAll('[data-bw-thumb]').forEach((t) => t.classList.toggle('is-current', t.dataset.mediaId === currentId));
    const count = this.querySelector('[data-bw-gallery-count]');
    if (count) count.textContent = `${this.#index + 1} / ${items.length}`;
  }
}

if (!customElements.get('bw-product')) customElements.define('bw-product', BwProduct);
